import React from "react";
import {
  Users, Car, Clock, ShieldAlert, Siren, LogIn, LogOut, UserCheck, Ban, RefreshCw,
} from "lucide-react";
import { panel } from "../../api/modulos.service";
import { Button } from "../shared/Button";
import { estilos as est, useCarga, Vacio, Aviso } from "./base";

/**
 * CENTRO DE SEGURIDAD — qué está pasando AHORA.
 *
 * Contesta la pregunta que antes no tenía respuesta: cuánta gente hay dentro
 * en este momento. Todo lo demás de la pantalla es contexto de eso.
 *
 * Se refresca sola cada treinta segundos. No es tiempo real por socket a
 * propósito: son cifras agregadas, no eventos, y un contador que parpadea cada
 * vez que alguien entra distrae más de lo que informa.
 */

const TARJETAS = [
  { clave: "personasDentro", etiqueta: "Personas dentro", icono: Users, tono: "ok" },
  { clave: "vehiculosDentro", etiqueta: "Vehículos dentro", icono: Car, tono: "ok" },
  { clave: "esperandoAutorizacion", etiqueta: "Esperando autorización", icono: Clock, tono: "aviso" },
  { clave: "accesosRechazadosHoy", etiqueta: "Accesos rechazados hoy", icono: Ban, tono: "malo" },
  { clave: "incidentesAbiertos", etiqueta: "Incidentes abiertos", icono: ShieldAlert, tono: "malo" },
  { clave: "sosActivos", etiqueta: "Emergencias activas", icono: Siren, tono: "malo" },
  { clave: "guardiasDeTurno", etiqueta: "Guardias de turno", icono: UserCheck, tono: "ok" },
  { clave: "restriccionesActivas", etiqueta: "Restricciones activas", icono: Ban, tono: "aviso" },
];

const TONOS = {
  ok: "var(--success)",
  aviso: "var(--warning)",
  malo: "var(--danger)",
};

const estilos = {
  rejilla: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(180px, 100%), 1fr))",
    gap: "var(--space-4)",
  },
  cifra: {
    display: "flex",
    alignItems: "center",
    gap: "var(--space-3)",
    padding: "var(--space-4)",
    borderRadius: "var(--radius-lg)",
    border: "1px solid var(--border)",
    backgroundColor: "var(--bg-elevated)",
  },
  numero: {
    margin: 0,
    fontFamily: "var(--font-display)",
    fontSize: "var(--text-3xl)",
    fontWeight: 700,
    lineHeight: 1,
    color: "var(--text)",
  },
  etiqueta: { margin: 0, fontSize: "var(--text-xs)", color: "var(--text-secondary)" },
  linea: {
    display: "flex",
    alignItems: "baseline",
    gap: "var(--space-3)",
    padding: "var(--space-3) 0",
    borderBottom: "1px solid var(--border-light)",
    fontSize: "var(--text-sm)",
  },
  hora: {
    flexShrink: 0,
    fontVariantNumeric: "tabular-nums",
    color: "var(--text-tertiary)",
    fontSize: "var(--text-xs)",
  },
};

const hora = (fecha) =>
  new Date(fecha).toLocaleTimeString("es-SV", {
    timeZone: "America/El_Salvador",
    hour: "2-digit",
    minute: "2-digit",
  });

export default function CentroSeguridad() {
  const { datos, cargando, error, recargar } = useCarga(panel.centroDeSeguridad, []);

  React.useEffect(() => {
    const t = setInterval(recargar, 30000);
    return () => clearInterval(t);
  }, [recargar]);

  /* El error va ANTES del «Cargando…»: esta pantalla se refresca sola cada
   * 30 segundos, y si la última vuelta falló hay que decirlo en vez de seguir
   * enseñando las cifras como si fueran las de ahora. */
  if (error)
    return (
      <Aviso>
        {error}
        <div style={{ marginTop: "var(--space-3)" }}>
          <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
            Reintentar
          </Button>
        </div>
      </Aviso>
    );
  if (cargando && !datos) return <Vacio>Cargando…</Vacio>;

  const ahora = datos?.ahora || {};

  return (
    <div style={est.seccion}>
      <div style={est.cabecera}>
        <div>
          <h2 style={est.titulo}>Centro de seguridad</h2>
          <p style={est.subtitulo}>
            Hoy han entrado {datos?.hoy?.entradas ?? 0} y han salido {datos?.hoy?.salidas ?? 0}.
            Se actualiza solo.
          </p>
        </div>
      </div>

      <div style={estilos.rejilla}>
        {TARJETAS.map(({ clave, etiqueta, icono: Icono, tono }) => {
          const valor = ahora[clave] ?? 0;
          // Sólo se enciende el color cuando el número pide atención: si todo
          // va en rojo, el rojo deja de significar nada.
          const color = valor > 0 ? TONOS[tono] : "var(--text-tertiary)";
          return (
            <div key={clave} style={estilos.cifra}>
              <Icono size={22} style={{ color, flexShrink: 0 }} />
              <div>
                <p style={{ ...estilos.numero, color: valor > 0 ? color : "var(--text)" }}>{valor}</p>
                <p style={estilos.etiqueta}>{etiqueta}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <h3 style={{ ...est.titulo, fontSize: "var(--text-lg)", marginBottom: "var(--space-3)" }}>
          Actividad reciente
        </h3>
        {(datos?.actividadReciente || []).length === 0 ? (
          <Vacio>Todavía no hay movimientos registrados hoy.</Vacio>
        ) : (
          <div>
            {datos.actividadReciente.map((a) => (
              <div key={a.id} style={estilos.linea}>
                <span style={estilos.hora}>{hora(a.hora)}</span>
                {/* `overflowWrap: anywhere` y `minWidth: 0`: el resumen es
                    texto que viene del servidor y puede no tener un solo
                    espacio donde partirse —un nombre largo, o una fila vieja
                    con el cifrado dentro—. Sin esto empujaba 652 px y la barra
                    horizontal aparecía en la página, no en la línea. */}
                <span style={{ color: "var(--text)", minWidth: 0, overflowWrap: "anywhere" }}>
                  {a.resumen}
                  {a.punto && (
                    <span style={{ color: "var(--text-tertiary)" }}> · {a.punto}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
